import React from 'react';

const PrivacyPolicy = () => {
  return (
    <div className="privacy-policy">
      <h1>Privacy Policy for DataSpeak LLC</h1>
      <p className="effective-date"><strong>Effective Date:</strong> 04/02/2025</p>

      <p className="intro">
        This Privacy Policy describes how DataSpeak LLC ("we," "us," or "our"),
        a company registered in the State of Georgia, collects, uses, discloses,
        and protects your information when you use our SaaS-based AI product
        ("Service"). By accessing or using our Service, you agree to the terms
        of this Privacy Policy.
      </p>

      <PolicySection title="1. Information We Collect">
        <p>We collect the following types of information:</p>
        <ul>
          <li><strong>Personal Information:</strong> Name, email address, phone number, company
              name, payment details, and other identifiers you provide during
              registration or use of our Service.</li>
          <li><strong>Usage Data:</strong> IP address, device information, browser type,
              operating system, access times, and pages viewed.</li>
          <li><strong>Cookies and Tracking Technologies:</strong> We use cookies and similar
              technologies to enhance your experience, analyze usage, and for
              security purposes.</li>
        </ul>
      </PolicySection>

      <PolicySection title="2. How We Collect Information">
        <ul>
          <li><strong>Directly from You:</strong> When you register, use our Service, contact
              support, or communicate with us.</li>
          <li><strong>Automatically:</strong> Through cookies, log files, and analytics tools
              as you interact with our Service.</li>
          <li><strong>From Third Parties:</strong> If you connect third-party services or
              accounts, we may receive information as permitted by those services.</li>
        </ul>
      </PolicySection>

      <PolicySection title="3. Use of Information">
        <p>We use your information to:</p>
        <ul>
          <li>Provide, operate, and maintain our Service.</li>
          <li>Improve, personalize, and expand our Service.</li>
          <li>Communicate with you, including for customer support and marketing.</li>
          <li>Process transactions and manage your account.</li>
          <li>Comply with legal obligations and enforce our terms.</li>
        </ul>
      </PolicySection>

      <PolicySection title="4. Sharing and Disclosure of Information">
        <p>We may share your information:</p>
        <ul>
          <li><strong>With Service Providers:</strong> For payment processing, analytics,
              hosting, and other business operations, under strict contractual
              obligations.</li>
          <li><strong>For Legal Reasons:</strong> To comply with applicable laws, regulations,
              or legal processes.</li>
          <li><strong>With Consent:</strong> If you authorize us to share your information with
              third parties.</li>
          <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or
              sale of all or a portion of our assets.</li>
        </ul>
        <p>We do <strong>not</strong> sell your personal information to third parties for their
        own marketing purposes.</p>
      </PolicySection>

      <PolicySection title="5. Data Security">
        <p>We implement appropriate technical and organizational measures to
        protect your information, including:</p>
        <ul>
          <li>Encryption of data in transit and at rest.</li>
          <li>Access controls and authentication.</li>
          <li>Regular security assessments and audits.</li>
          <li>Employee training and confidentiality agreements.</li>
        </ul>
      </PolicySection>

      <PolicySection title="6. Data Retention">
        <p>We retain your personal information only as long as necessary to fulfill
        the purposes outlined in this policy, comply with legal obligations,
        resolve disputes, and enforce our agreements. When no longer needed,
        data is securely deleted or anonymized.</p>
      </PolicySection>

      <PolicySection title="7. User Rights">
        <p>Depending on your jurisdiction, you may have the right to:</p>
        <ul>
          <li>Access the personal information we hold about you.</li>
          <li>Request correction or deletion of your data.</li>
          <li>Object to or restrict certain processing.</li>
          <li>Withdraw consent at any time, where processing is based on consent.</li>
          <li>Request data portability.</li>
        </ul>
        <p>To exercise your rights, contact us at &lt;info@dataspeak.co&gt;.</p>
      </PolicySection>

      <PolicySection title="8. Cookies and Tracking">
        <p>We use cookies and similar technologies for analytics, personalization,
        and security. You can manage your cookie preferences through your
        browser settings.</p>
      </PolicySection>

      <PolicySection title="9. International Data Transfers">
        <p>If we transfer your data outside the United States, we ensure
        appropriate safeguards are in place to protect your information in
        accordance with applicable law.</p>
      </PolicySection>

      <PolicySection title="10. Children's Privacy">
        <p>Our Service is not intended for children under 13. We do not knowingly
        collect personal information from children under 13. If you believe a
        child has provided us with personal information, please contact us for
        removal.</p>
      </PolicySection>

      <PolicySection title="11. Changes to This Policy">
        <p>We may update this Privacy Policy from time to time. We will notify you
        of any material changes by posting the new policy on our website and
        updating the effective date. Continued use of the Service after changes
        constitute acceptance of the revised policy.</p>
      </PolicySection>

      <PolicySection title="12. Contact Us">
        <div className="contact-info">
          <p>For questions or concerns about this Privacy Policy or our data
          practices, contact:</p>
          <p><strong>DataSpeak LLC</strong><br />
          &lt;info@dataspeak.co&gt;</p>
        </div>
      </PolicySection>

      <PolicySection title="Legal Compliance and Limitation of Liability">
        <p>We comply with all applicable data protection laws, including the
        Georgia Consumer Privacy Protection Act (SB 111, pending), and the
        Georgia Data Protection Law, which require transparency, user rights,
        and robust security measures.</p>
      </PolicySection>

      <style jsx>{`
        .privacy-policy {
          max-width: 800px;
          margin: 0 auto;
          padding: 2rem;
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          line-height: 1.6;
          color: #000;
        }

        h1 {
          color: #E07C2D;
          font-size: 2rem;
          margin-bottom: 0.5rem;
          font-weight: 700;
        }

        .effective-date {
          font-weight: 600;
          margin-bottom: 2rem;
          color: #666;
        }

        .intro {
          margin-bottom: 2rem;
        }

        h2 {
          color: #E07C2D;
          font-size: 1.5rem;
          margin: 2.5rem 0 1rem 0;
          font-weight: 600;
        }

        ul {
          padding-left: 1.5rem;
          margin: 1rem 0;
        }

        li {
          margin-bottom: 0.5rem;
          color: #000;
        }

        strong {
          color: #E07C2D;
          font-weight: 600;
        }

        .contact-info {
          background-color: rgba(224, 124, 45, 0.08);
          padding: 1.25rem;
          border-radius: 6px;
          margin: 1.5rem 0;
          border-left: 3px solid #E07C2D;
        }

        @media (max-width: 768px) {
          .privacy-policy {
            padding: 1.5rem;
          }

          h1 {
            font-size: 1.75rem;
          }

          h2 {
            font-size: 1.3rem;
          }
        }
      `}</style>
    </div>
  );
};

// Renamed for clarity and consistency
const PolicySection = ({ title, children }) => {
  return (
    <section>
      <h2>{title}</h2>
      {children}
    </section>
  );
};

export default PrivacyPolicy;